
import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
} from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Lightbulb, Zap, type LucideIcon } from 'lucide-react';

import { cn } from '@/lib/utils';

export type ChatMode = 'plan' | 'execute';

interface AgentModeDropdownProps {
  mode: ChatMode;
  onChange: (mode: ChatMode) => void;
  disabled?: boolean;
  /** Collapse to an icon-only button (no text label) when space is tight */
  compact?: boolean;
}

const modeConfig: Record<
  ChatMode,
  { label: string; description: string; icon: LucideIcon }
> = {
  plan: {
    label: 'Plan',
    description: 'Analyze without changing files',
    icon: Lightbulb,
  },
  execute: {
    label: 'Execute',
    description: 'Write and modify code',
    icon: Zap,
  },
};

export function AgentModeDropdown({
  mode,
  onChange,
  disabled = false,
  compact = false,
}: AgentModeDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPos, setMenuPos] = useState<{
    left: number;
    bottom: number;
  } | null>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      // Menu is portaled to body, so check it separately from the trigger
      if (
        !dropdownRef.current?.contains(target) &&
        !menuRef.current?.contains(target)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Position the portaled menu above the trigger; it opens upward (bottom-anchored)
  const MENU_WIDTH = 220;
  useLayoutEffect(() => {
    if (!isOpen) return;
    const updatePosition = () => {
      const el = triggerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const left = Math.max(
        8,
        Math.min(rect.left, window.innerWidth - MENU_WIDTH - 8),
      );
      setMenuPos({ left, bottom: window.innerHeight - rect.top + 6 });
    };
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen]);

  const handleSelect = (newMode: ChatMode) => {
    onChange(newMode);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        title={compact ? modeConfig[mode].label : undefined}
        className={cn(
          'inline-flex items-center gap-1.5 h-7 rounded-md justify-between',
          compact ? 'px-1.5' : 'pl-2.5 pr-1.5',
          'border border-border bg-background dark:bg-muted/40 hover:bg-muted/80 hover:border-border',
          'text-foreground',
          disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
          isOpen && 'bg-muted/80',
          'transition-colors duration-150 text-xs font-medium',
        )}
      >
        {compact ? (
          React.createElement(modeConfig[mode].icon, {
            className: 'w-3.5 h-3.5',
          })
        ) : (
          <span>{modeConfig[mode].label}</span>
        )}
        <ChevronDown
          className={cn(
            'w-3 h-3 text-muted-foreground transition-transform',
            isOpen && 'rotate-180',
          )}
        />
      </button>

      {isOpen &&
        menuPos &&
        createPortal(
          <div
            ref={menuRef}
            style={{
              position: 'fixed',
              left: menuPos.left,
              bottom: menuPos.bottom,
              width: MENU_WIDTH,
            }}
            className="bg-popover rounded-lg shadow-lg border border-border z-[9999]"
          >
            <div className="px-1.5 py-1.5 flex flex-col gap-1">
            {(Object.entries(modeConfig) as [ChatMode, typeof modeConfig['plan']][]).map(
              ([key, config]) => {
                const isSelected = mode === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => handleSelect(key)}
                    className={cn(
                      'w-full text-left px-2.5 py-1.5 rounded-md transition-colors duration-150',
                      'flex flex-col items-start',
                      isSelected
                        ? 'bg-primary/10 dark:bg-primary/20 ring-1 ring-inset ring-primary/40 dark:ring-primary/60'
                        : 'hover:bg-foreground/[0.04] dark:hover:bg-foreground/[0.06]',
                    )}
                  >
                    <span
                      className={cn(
                        'text-sm font-medium leading-tight',
                        isSelected
                          ? 'text-primary dark:text-primary-foreground'
                          : 'text-foreground',
                      )}
                    >
                      {config.label}
                    </span>
                    <span className="text-[11px] text-muted-foreground leading-tight">
                      {config.description}
                    </span>
                  </button>
                );
              },
            )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
